import React, { useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { message, notification, Button, Modal } from 'antd';
import { FileTextOutlined } from '@ant-design/icons';
import uuid from 'react-uuid';
import _ from 'lodash';
import Papa from 'papaparse';

// Imports from original file
import { importApi } from "services/actions/import";
import { validateCSVColumns } from "utils/validate-csv-columns";
import { exportFormData } from "hooks/exportForm";
import convertToIndex from "components/Utils/convertToIndex";

const { confirm } = Modal;

const useCSVOperations = ({
  name,
  fields,
  user,
  query,
  sorterBy,
  location,
  navigate,
  formRef,
  isRegistryForm = false,
  setExportModalVisible,
  totalRecords = 0,
}) => {
  const dispatch = useDispatch();
  const [importRecords, setImportRecords] = useState(false);
  const [disableImport, setDisableImport] = useState(false);
  const [resetUploader, setResetUploader] = useState(false);
  const [queueId, setQueueId] = useState(uuid());
  const [exportQueueId, setExportQueueId] = useState(null);
  const [exportResult, setExportResult] = useState(null);
  const exportLoading = useSelector(state => state.exportRecords.loading);

  const openImport = () => {
    setImportRecords(true);
  };

  const closeImport = () => {
    setImportRecords(false);
  };

  const handleImportFileUpload = (file) => {
    const MAX_SIZE_MB = 20;
    const isLtMaxSize = file.size / 1024 / 1024 <= MAX_SIZE_MB;

    if (!isLtMaxSize) {
      notification.error({
        message: 'Arquivo Recusado',
        description: `O arquivo escolhido excede o limite máximo permitido de ${MAX_SIZE_MB}MB. Por favor, divida o arquivo e tente novamente.`,
        duration: 5,
        placement: 'topRight',
      });
      return;
    }

    setDisableImport(true);
    const reader = new FileReader();

    reader.onload = async (e) => {
      const uploadQueueId = uuid();
      setQueueId(uploadQueueId);
      const csvData = e.target.result
        .replace(/\r/g, "")
        .split("\n")
        .map((row) => row.split(";"))
        .filter((item) => item[0] !== "_id");

      const requiredColumns = _.chain(fields)
        .filter(item => {
          if (['label'].includes(item.fieldtype)) {
            return false;
          } else {
            return !item.isReadOnly;
          }
        })
        .map("field")
        .filter((item) => item !== "_id")
        .concat(["createdAt"])
        .value();

      const validColumns = [...requiredColumns, "updatedAt"];
      const missingColumns = validateCSVColumns(csvData, requiredColumns);

      if (missingColumns.length > 0) {
        message.error(
          `As colunas a seguir estão faltando no arquivo CSV: ${missingColumns.join(", ")}`
        );
        setDisableImport(false);
        setResetUploader(true);
      } else {
        const extraColumns = csvData[0].filter(
          (column) => !validColumns.includes(column) && column !== "" && column !== "updatedAt"
        );
        if (extraColumns.length > 0) {
          message.error(
            `O arquivo CSV contém colunas extras: ${extraColumns.join(", ")}`
          );
          setDisableImport(false);
          setResetUploader(true);
        } else {
          if (csvData.length <= 1) {
            message.error(
              "O CSV está vazio. Por favor, forneça um CSV com pelo menos 1 dado."
            );
            setDisableImport(false);
            return;
          }



          setDisableImport(false);
          setResetUploader(true);
          setImportRecords(false);
          const createdBy = user?.sub || user?.uuid || user?.id;
          if (!createdBy) {
            message.error("Nao foi possivel identificar o usuario da importacao.");
            return;
          }
          importApi.importSingleFile({
            file: file,
            formName: name,
            queueId: uploadQueueId,
            createdBy,
          })
            .then((response) => {
              notification.destroy();
              const responseQueueId = response?.queueId || uploadQueueId;
              setQueueId(responseQueueId);
              navigate(
                `/formularios/importar/detalhes/${responseQueueId}?form=${name}`
              );
            })
            .catch((error) => {
              console.error(error);
              message.error(`${file.name} não foi enviado, tente novamente!`);
            });
        }
      }
    };

    reader.readAsText(file);
  };

  const handleExportModel = () => {
    const data = [];
    const allColumns = _.chain(fields)
      .filter(item => {
        if (['label'].includes(item.fieldtype)) {
          return false;
        } else {
          return !item.isReadOnly;
        }
      })
      .map("field")
      .filter((item) => item !== "_id")
      .concat(["createdAt", "updatedAt"])
      .value();

    const csvData = Papa.unparse(
      { fields: allColumns, data: data },
      { delimiter: ";" }
    );
    // BOM UTF-8 para o Excel interpretar a acentuação dos cabeçalhos corretamente
    const csvBlob = new Blob(["﻿", csvData], {
      type: "text/csv;charset=utf-8;",
    });
    const csvUrl = URL.createObjectURL(csvBlob);
    const downloadLink = document.createElement("a");
    downloadLink.href = csvUrl;
    const currentForm = decodeURIComponent(name);
    downloadLink.setAttribute(
      "download",
      `${convertToIndex(currentForm.replace("obj_", ""))}-modelo.csv`
    );
    downloadLink.click();
  };

  const handleExportCSV = () => {
    if (totalRecords === 0) {
      notification.warning({
        message: 'Aviso',
        description: 'Não há registros para serem exportados.',
        duration: 8,
      });
      return;
    }

    if (totalRecords > 500000) {
      notification.warning({
        message: 'Limite de exportação excedido',
        description: `Não é possível exportar mais de 500 mil registros simultaneamente (sua busca atual possui ${totalRecords.toLocaleString('pt-BR')} registros). Por favor, aplique filtros para reduzir os resultados e tente novamente.`,
        duration: 8,
      });
      return;
    }

    confirm({
      title: "Iniciar exportação?",
      icon: <FileTextOutlined />,
      content: "Por favor, esteja ciente de que se o formulário contiver um grande número de registros, o processo pode levar algum tempo.",
      onOk() {
        const nextQueueId = `export_${Date.now()}`;
        setExportQueueId(nextQueueId);
        setExportResult(null);
        if (setExportModalVisible) setExportModalVisible(true);
        // Pega o createdAt diretamente do formulário de filtros (ProTable)
        let csvCreatedAt = null;
        if (formRef?.current) {
          if (typeof formRef.current.getFieldValue === 'function') {
            csvCreatedAt = formRef.current.getFieldValue("createdAt");
          } else if (typeof formRef.current.getFieldsValue === 'function') {
            const vals = formRef.current.getFieldsValue();
            if (vals) csvCreatedAt = vals.createdAt;
          }
        }

        const exportPromise = dispatch(
          exportFormData(
            name,
            query.current,
            query.pageSize,
            csvCreatedAt,
            query.queryParams || {},
            user.sub,
            sorterBy,
            location,
            navigate,
            formRef,
            isRegistryForm,
            nextQueueId
          )
        );

        Promise.resolve(exportPromise)
          .then((result) => {
            if (!result) return;
            if (result.success && result.downloadUrl) {
              setExportResult({
                status: "success",
                downloadUrl: result.downloadUrl,
                message: result.message
              });
            } else if (result.success === false) {
              setExportResult({
                status: "error",
                message: result.error || "Erro ao processar exportação."
              });
            }
          })
          .catch((error) => {
            setExportResult({
              status: "error",
              message: error?.message || "Erro ao processar exportação."
            });
          });
      },
    });
  };

  return {
    importRecords,
    disableImport,
    resetUploader,
    queueId,
    openImport,
    closeImport,
    handleImportFileUpload,
    handleExportModel,
    handleExportCSV,
    exportLoading,
    exportQueueId,
    exportResult
  };
};

export default useCSVOperations;
